<script setup>
  import { ref, computed, useTemplateRef, watchEffect, watch, onMounted } from 'vue';
  import { useTidesStore } from "@/stores/TidesStore";
  import TideGraphNowMarker from "@/components/TideGraphNowMarker.vue";
  import { useMediaQuery } from '@/composables/useMediaQuery';
  import { useUserStore } from '@/stores/UserStore'

  const weekGraphContainer = useTemplateRef('week-graph-container');

  const graphPlotLeft = defineModel('plotLeftOffset', { type: Number, default: 0 });
  const graphPlotTop = defineModel('plotTopOffset', { type: Number, default: 0 });
  const graphPlotHeight = defineModel('plotHeight', { type: Number, default: 0 });

  const props = defineProps({
    startDate: {
      type: Date,
      required: true
    },
    endDate: {
      type: Date,
      required: true
    },
    containerId: {
      type: String,
      required: true
    },
    weekCount: {
      type: Number,
      default: 1
    },
    storeId: {
      type: Number,
      default: null
    }
  });

  const tideFillColor = {
    linearGradient: {
      x1: 0,
      x2: 0,
      y1: 0,
      y2: 1
    },
    stops: [
      [0, 'hsla(206, 94%, 87%, 1.0)'],
      [1, 'hsla(206, 94%, 87%, 0)']
    ]
  };

  const currentsFillColor = {
    linearGradient: {
      x1: 0,
      x2: 0,
      y1: 0,
      y2: 1
    },
    stops: [
      [0, 'hsla(286, 92%, 86%, 1.0)'],
      [0.5, 'hsla(286, 92%, 86%, 0)'],
      [1, 'hsla(286, 92%, 86%, 1.0)'],
    ]
  };

  const graphColorInfo = {
    tide: {
      color: 'hsla(211, 86%, 35%, 1.0)',
      fillColor: tideFillColor
    },
    currents: {
      color: 'hsla(287, 100%, 35%, 1.0)',
      fillColor: currentsFillColor
    }
  };

  const tidesStore = useTidesStore(props.storeId);
  const userStore = useUserStore();

  const isDesktop = useMediaQuery('(min-width: 840px)');

  const graph = ref(null);
  const tideData = ref(null);
  const tideDataMin = ref(null);
  const tideDataMax = ref(null);
  const nowTideValue = ref(null);
  const nowMarkerPosition = ref(null);
  const showGraph = ref(true);

  const tideSpotType = computed(() => tidesStore.selectedNearbyTideSpot.type);
  const lineColor = computed(() => graphColorInfo[tideSpotType.value]?.color);
  const fillColor = computed(() => graphColorInfo[tideSpotType.value]?.fillColor);
  const graphUnit = computed(() => tidesStore.selectedNearbyTideSpot.type === 'tide' ? userStore.units.height : 'kts');  
  const now = computed(() => Date.now());
  const solunarEvents = computed(() => tidesStore.getDateRangeSolunarEvents(props.startDate, props.endDate));

  const solunarEventGraphPoints = computed(() => {
    if (!graph.value || !solunarEvents.value || solunarEvents.value.length === 0) return [];

    return solunarEvents.value.map(event => {
      if (!event.eventName.toLowerCase().includes('sunset') && !event.eventName.toLowerCase().includes('sunrise') && !event.eventName.toLowerCase().includes('moonset') && !event.eventName.toLowerCase().includes('moonrise')) return null;
      
      const eventDateTime = event.dateTime;
      const iconName  = event.eventName.toLowerCase().replaceAll(',', '').replaceAll(' ', '-');
      const graphPoint = graph.value.series[0].points.find(point => {
        return point.x === eventDateTime
      });

      if (graphPoint) {
          graphPoint.tideEventName = event.eventName;
          graphPoint.tideEventValue = event.eventValue;
          graphPoint.tideEventIconName = iconName;
          return graphPoint;
      }
      
      return null;
    }).filter(point => point !== null);
  });  

  function initializeWeekGraph() {
    if (!weekGraphContainer.value) return;

    graph.value = Highcharts.chart(weekGraphContainer.value, {
      chart: {
          type: 'areaspline',
          height: isDesktop.value ? '70px' : '80px',
          marginTop: isDesktop.value ? 20 : 30,
          marginRight: 0,
          marginLeft: 30,
          spacingBottom: 0,
          backgroundColor: 'transparent',
          events: {
            load: function () {
              graph.value = this;
            },
            render: function () {
              graphPlotLeft.value = this.plotLeft;
              graphPlotHeight.value = this.plotHeight;
              graphPlotTop.value = this.plotTop;

              updateNowPosition();
              drawNightBlocks();
            }
          }          
        },
      credits: {
          enabled: false,
      },
      legend: {
          enabled: false,
      },      
      xAxis: {
          type: 'datetime',
          dateTimeLabelFormats: {
            day: '%l %p',
            hour: '%l %p',
            minute: '%l:%M %p',
            millisecond: '%l:%M %p',
          },
          gridLineWidth: 0,
          lineColor: 'hsl(0, 0%, 90%, 1)',
          labels: {
            enabled: false,
            useHTML: true,
            formatter: function () {
                const label = this.axis.defaultLabelFormatter.call(this);
                const isTimeLabel = label.indexOf('AM') > -1 || label.indexOf('PM') > -1;

                if (!isTimeLabel) {
                  return null;
                }

                const dayPeriod = label.indexOf('AM') > -1 ? 'A' : 'P';
                const time = label.split(' ')[0];

                return `<span class="jwx-graph__label-text">${time}${dayPeriod}</span>`;
            },
            step: 1,
          },
          minPadding: 0,
          maxPadding: 0,
          tickInterval: 3600 * 1000 * 6, // six hours
          tickLength: 0,
          showFirstLabel: false,
          endOnTick: false,
      },
      yAxis: {
        labels: {
          distance: 0,
          formatter: function () {
            return `<span class="jwx-tide-graph__y-axis-label">${this.value}</span>`;
          },
          padding: 0,
          overflow: 'allow',
          useHTML: true,
          x: -10,
        },
        max: tideDataMax.value && tideSpotType.value === 'currents' ? Math.ceil(tideDataMax.value) + 1 : null,
        min: tideDataMin.value && tideSpotType.value === 'currents' ? Math.floor(tideDataMin.value) - 1 : 0,
        maxPadding: tideSpotType.value === 'tide' ? .5 : null,
        showFirstLabel:  false,
        showLastLabel:  false,
        tickInterval:  graphUnit.value === 'm' ? .5 : tideDataMax.value <= 2 ? 1 : 2,
        tickLength: 0,
        title: {
          enabled: false
        },
      },
      plotOptions: {
        series: {
          animation: false,
          states: {
            inactive: {
              opacity: 1
            }
          },
          point: {
            events: {
              mouseOver: () => false,
              mouseOut: () => false
            }
          }
        }
      },      
      series: [{
        name: 'Tide Data',
        data: tideData.value,
        color: lineColor.value,
        fillColor: fillColor.value,
        lineWidth: 4,
        pointStart: tideData.value?.length ? tideData.value[0].dateTime : null,
        zoneAxis: 'x',
        zones: [{
          value: now.value,
          color: 'hsla(0, 0%, 61%, 1.0)'
        }, {
          color: lineColor.value,
        }]          
      }],
      title: {
          text: null,
      },
      tooltip: {
          enabled: false,
      },
    });
  }

  function updateNowPosition() {
    const [interpolatedYValue, beforeIndex, afterIndex] = getInterpolatedYValue(Date.now(), tideData.value);

    if (isNaN(interpolatedYValue) || beforeIndex === null || afterIndex === null || beforeIndex === -1 || afterIndex === -1) { 
      nowMarkerPosition.value = null; 
      return; 
    } 

    graph.value.series[0].addPoint([now.value, interpolatedYValue], false);
    nowTideValue.value = interpolatedYValue;

    const x = graph.value.xAxis[0].toPixels(now.value) - graph.value.plotLeft;
    const y = graph.value.series[0].yAxis.toPixels(interpolatedYValue);

    nowMarkerPosition.value = { x, y };
  }  

  function addSolunarEventGraphPoints() {
    if (!solunarEvents.value || solunarEvents.value.length === 0) return;

    solunarEvents.value.forEach(point => {
      const eventTime = point.dateTime;
      const [interpolatedYValue, beforeIndex, afterIndex] = getInterpolatedYValue(eventTime, tideData.value);

      if (isNaN(interpolatedYValue) || beforeIndex === null || afterIndex === null || beforeIndex === -1 || afterIndex === -1) return;

      const dataPoint = [eventTime, interpolatedYValue];
      tideData.value.splice(afterIndex, 0, dataPoint);
    });
  }

  function drawNightBlocks() {
    const sunEvents = solunarEventGraphPoints.value.filter(event => event.tideEventName.toLowerCase().includes('sun'));
    const y = 0;
    const height = graph.value.series[0].yAxis.height + graph.value.plotTop;
    const graphPoints = graph.value.series[0].points;
    const startXValue = graphPoints[0].x;
    const endXValue = graphPoints[graphPoints.length - 1].x;
    let x1 = graph.value.xAxis[0].toPixels(startXValue);
    let x2 = null;
    let width = 0;
    
    for (let i = 0; i < sunEvents.length; i++) {
      const event = sunEvents[i];

      if (event.tideEventName.toLowerCase().includes('sunrise')) {
        if (i === 0) {
          x1 = graph.value.xAxis[0].toPixels(startXValue);
          x2 = graph.value.xAxis[0].toPixels(event.x);
        } else {
          continue;
        }
      }

      if (event.tideEventName.toLowerCase().includes('sunset')) {
        if (i === sunEvents.length - 1) {
          x1 = graph.value.xAxis[0].toPixels(event.x);
          x2 = graph.value.xAxis[0].toPixels(endXValue);
        } else {
          x1 = graph.value.xAxis[0].toPixels(event.x);
          x2 = graph.value.xAxis[0].toPixels(sunEvents[i + 1].x);
        }
      }

      width = x2 - x1;

      graph.value.renderer.rect(x1, y, width, height)
        .attr({
          fill: 'hsla(0, 0%, 90%, 0.4)',
          zIndex: 1
        })
        .add();
    }
  } 

  async function updateTideData() {
    if (!tidesStore.selectedNearbyTideSpot) return;

    const responseData = await tidesStore.getAllTideDataByDateRange({
      tide_site_name: tidesStore.selectedNearbyTideSpot.location,
      begin_time: props.startDate,
      end_time: props.endDate,
      date_range_count: props.weekCount
    });

    const combinedData = [];
    Object.values(responseData.tides.data).forEach(dataSet => combinedData.push(...dataSet));

    if (!responseData || !responseData.tides || !responseData.tides.data || Object.keys(responseData.tides.data).length === 0) {
      showGraph.value = false;
      return;
    } else {
      showGraph.value = true;
    }

    const thinnedData = [];
    for (let i = 0; i < combinedData.length; i += 45) {
      const point = combinedData[i];

      thinnedData.push(point);
    }

    tideData.value = thinnedData;
    tideDataMin.value = responseData.tides.minValue;
    tideDataMax.value = responseData.tides.maxValue;

    addSolunarEventGraphPoints();
  }

  function getInterpolatedYValue(dateTime, dataSet) {
    let [beforeIndex, afterIndex] = getBeforeAndAfterDateTimeIndexes(dateTime, dataSet);

    if (beforeIndex === null || afterIndex === null || beforeIndex === -1 && afterIndex === -1) return [null, null, dataSet];

    const beforePointIndex = beforeIndex === -1 ? 0 : beforeIndex;
    const afterPointIndex = afterIndex === -1 ? dataSet.length - 1 : afterIndex;

    let beforePoint;
    let afterPoint;

    if (typeof dataSet[beforePointIndex] === 'object' && Object.prototype.hasOwnProperty.call(dataSet[beforePointIndex], 'x')) {
      beforePoint = dataSet[beforePointIndex]  ;
      afterPoint = dataSet[afterPointIndex];
    } else {
      beforePoint = { x: dataSet[beforePointIndex][0], y: dataSet[beforePointIndex][1] };
      afterPoint = { x: dataSet[afterPointIndex][0], y: dataSet[afterPointIndex][1] };
    };

    const nowBeforeTimeDelta = dateTime - beforePoint.x;
    const currentTimePeriodLength = afterPoint.x - beforePoint.x;
    const currentTimePeriodCompleteFactor = nowBeforeTimeDelta / currentTimePeriodLength;
    const interpolatedYValue = ((afterPoint.y - beforePoint.y) * currentTimePeriodCompleteFactor) + beforePoint.y;

    return [interpolatedYValue, beforeIndex, afterIndex];
  }    

  function getBeforeAndAfterDateTimeIndexes(dateTime, dataSet) {
    const dateProp = typeof dataSet[0] === 'object' && Object.prototype.hasOwnProperty.call(dataSet[0], 'x') ? 'x' : 0;
    const afterIndex = dataSet.findIndex(element => element[dateProp] > dateTime);
    let beforeIndex = null;

    for (let i = 0; i < dataSet.length; i++) {
      if (i === 0 && dateTime < dataSet[i][dateProp]) {
        beforeIndex = -1;
        break;
      }

      if (dataSet[i][dateProp] < dateTime) beforeIndex = i;
    }

    return [beforeIndex, afterIndex];
  }  

  async function reload() {
    if (graph.value) {
      graph.value.destroy();
      graph.value = null;
    }

    await updateTideData();

    initializeWeekGraph();
  }

  watchEffect(() => {
    if (!weekGraphContainer.value || !tideData.value) return;

    addSolunarEventGraphPoints();
    initializeWeekGraph();
  });

  onMounted(async () => {
    watchEffect(async () => {
      if (!props.startDate || !props.endDate || !tidesStore.selectedNearbyTideSpot || !tidesStore.selectedNearbyTideSpot.location) return;
  
      await updateTideData();
    });
  });

  defineExpose({ reload });
</script>

<template>
  <div v-if="showGraph" ref="week-graph-container" :key="props.containerId" class="jwx-graph-calendar__week-graph"></div>
  <div v-if="showGraph" class="jwx-tide-graph__setup-block">
    <TideGraphNowMarker
      v-if="nowMarkerPosition"
      :position="nowMarkerPosition"
      :graph="graph"
    />
  </div>  
</template>